import { module as BaseModule } from "modujs";
import { createElement } from "react";
import { createRoot } from "react-dom/client";
import MobileMenuOverlay from "../components/MobileMenuOverlay.jsx";

const SELECTORS = {
  trigger: "[data-mobile-menu-trigger]",
};

const BREAKPOINT_QUERY = "(min-width: 1024px)";

export default class MobileMenu extends BaseModule {
  init() {
    this.trigger = this.el.querySelector(SELECTORS.trigger);

    if (!this.trigger) {
      return;
    }

    this.props = this.parseProps();
    this.mountTarget = document.createElement("div");
    this.mountTarget.className = "g-header__mobile-menu-root";
    this.el.appendChild(this.mountTarget);

    this.isOpen = false;

    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleMediaChange = this.handleMediaChange.bind(this);

    this.attachTriggerListener();
    this.attachMediaQueryListener();
    this.render();
  }

  attachTriggerListener() {
    if (!this.trigger) {
      return;
    }

    this.trigger.addEventListener("click", this.handleTriggerClick);
  }

  detachTriggerListener() {
    if (!this.trigger) {
      return;
    }

    this.trigger.removeEventListener("click", this.handleTriggerClick);
  }

  attachMediaQueryListener() {
    if (typeof window === "undefined" || !window.matchMedia) {
      return;
    }

    this.mediaQuery = window.matchMedia(BREAKPOINT_QUERY);
    this.mediaQuery.addEventListener("change", this.handleMediaChange);
    this.handleMediaChange(this.mediaQuery);
  }

  detachMediaQueryListener() {
    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener("change", this.handleMediaChange);
      this.mediaQuery = null;
    }
  }

  handleTriggerClick(event) {
    event.preventDefault();
    this.isOpen = true;
    this.render();
  }

  handleClose() {
    if (!this.isOpen) {
      return;
    }

    this.isOpen = false;
    this.render();
  }

  handleMediaChange(event) {
    if (event.matches && this.isOpen) {
      this.handleClose();
    }
  }

  parseProps() {
    const rawProps = this.el.getAttribute("data-mobile-menu-props");
    const defaultProps = {
      cta: {
        url: "/free-estimate",
        label: "Free Estimate",
        title: "Free Estimate",
        newWindow: false,
      },
      phoneLabel: "Or, give us a call!",
      phoneNumber: null,
      phoneHref: null,
      navigation: [],
      secondaryNavigation: [],
      bannerCta: null,
    };

    if (!rawProps) {
      return defaultProps;
    }

    try {
      const parsed = JSON.parse(rawProps);
      return {
        ...defaultProps,
        ...parsed,
        cta: { ...defaultProps.cta, ...(parsed?.cta || {}) },
        phoneLabel: parsed?.phoneLabel || defaultProps.phoneLabel,
        phoneNumber: parsed?.phoneNumber || defaultProps.phoneNumber,
        phoneHref: parsed?.phoneHref || defaultProps.phoneHref,
        navigation: Array.isArray(parsed?.navigation) ? parsed.navigation : defaultProps.navigation,
        secondaryNavigation: Array.isArray(parsed?.secondaryNavigation) ? parsed.secondaryNavigation : defaultProps.secondaryNavigation,
        bannerCta: parsed?.bannerCta || defaultProps.bannerCta,
      };
    } catch (error) {
      console.warn("Failed to parse mobile menu props:", error);
      return defaultProps;
    }
  }

  render() {
    if (!this.mountTarget) {
      return;
    }

    if (!this.reactRoot) {
      this.reactRoot = createRoot(this.mountTarget);
    }

    this.reactRoot.render(
      createElement(MobileMenuOverlay, {
        isOpen: this.isOpen,
        onClose: this.handleClose,
        cta: this.props?.cta,
        phoneLabel: this.props?.phoneLabel,
        phoneNumber: this.props?.phoneNumber,
        phoneHref: this.props?.phoneHref,
        navigation: this.props?.navigation || [],
        secondaryNavigation: this.props?.secondaryNavigation || [],
        bannerCta: this.props?.bannerCta || null,
      })
    );
  }

  destroy() {
    this.detachTriggerListener();
    this.detachMediaQueryListener();

    if (this.reactRoot) {
      this.reactRoot.unmount();
      this.reactRoot = null;
    }

    if (this.mountTarget) {
      this.mountTarget.remove();
      this.mountTarget = null;
    }

    this.trigger = null;
    this.isOpen = false;
  }
}
